import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import {
  EmbedType,
  GeneralThemeAndControls,
  IThemeGeneratorContext,
} from '@/theme/types'
import { getCache, removeCache, setCache } from '@/utils/cache'
import useQuery from '@/router/useQuery'
import { FunctionComponentChildren } from '@/@types/react'
import { defaultThemeCnx } from '../constants'

const ThemeGeneratorContext = createContext<IThemeGeneratorContext>(null)

export const ThemeGeneratorProvider: FunctionComponentChildren = ({
  children,
}) => {
  const query = useQuery()

  const [theme, setTheme] = useState<GeneralThemeAndControls>(() => {
    const theme = getCache<GeneralThemeAndControls>('colors')
    if (theme) {
      return theme
    }

    return defaultThemeCnx
  })

  const embed = useMemo<EmbedType>(() => {
    const storageEmbed = getCache<EmbedType>('embed')
    if (storageEmbed) return storageEmbed

    const embedFromQuery = {
      value: !!query.get('embed') || !!query.get('embedFull'),
      full: !!query.get('embedFull'),
      navbarEmbed: !!query.get('navbarEmbed'),
    }

    return embedFromQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const storageEmbed = getCache<EmbedType>('embed')
    if (storageEmbed) return

    setCache('embed', {
      value: !!query.get('embed') || !!query.get('embedFull'),
      full: !!query.get('embedFull'),
      navbarEmbed: !!query.get('navbarEmbed'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const withoutInteractionAttendance = useMemo(() => {
    const value = !!query.get('flagDesativaInteracaoPacienteSalaDeEspera')

    return {
      value,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setThemeColors = useCallback(
    (themeColors: GeneralThemeAndControls | null): void => {
      if (themeColors) {
        setTheme(themeColors)
        setCache('colors', themeColors)
      } else {
        setTheme(defaultThemeCnx)
        removeCache('colors')
      }
    },
    [],
  )

  const values = useMemo(
    () => ({
      theme,
      setThemeColors,
      embed: embed,
      withoutInteractionAttendance,
    }),
    [theme, setThemeColors, embed, withoutInteractionAttendance],
  )

  return (
    <ThemeGeneratorContext.Provider value={values}>
      {children}
    </ThemeGeneratorContext.Provider>
  )
}

export const useThemeGeneratorContext = (): IThemeGeneratorContext => {
  const context = useContext(ThemeGeneratorContext)
  if (!context) {
    throw new Error(
      'useAuthContext must be used within a ThemeGeneratorProvider',
    )
  }
  return context
}
